import HeaderButton from '@/components/elements/HeaderButton/HeaderButton';
import { HeaderButtonProps } from '@/components/elements/HeaderButton/HeaderButton.types';
import MerchantLogo from '@/components/elements/MerchantLogo/MerchantLogo';
import { MerchantLogoProps } from '@/components/elements/MerchantLogo/MerchantLogo.types';
import { USER_INFO_BANNER_OFFSET, Z_INDEX } from '@/constants/styleConstants';
import { useBookingFlowChrome } from '@/features/booking-flow/BookingFlowChromeContext';
import { classnames } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { HTMLAttributes, useRef } from 'react';
import { useHistory } from 'react-router-dom';

export const SEARCH_HEADER_CONTENT_HEIGHT = {
  DESKTOP: 72,
  MOBILE: 64,
} as const;

export type CheckoutHeaderProps = {
  leftSlot?: { button: HeaderButtonProps; text?: string };
  middleSlot: { title: string; subtitle?: string };
  rightSlot?: Pick<MerchantLogoProps, 'imgUrl' | 'variant'>;
};

const CheckoutHeader = ({ leftSlot, middleSlot, rightSlot }: CheckoutHeaderProps) => {
  const { isMobileView } = useMobileView();
  const history = useHistory();
  // Honoured here rather than by each page so every header the widget mounts
  // drops the logo, including pages that never heard of the opt-out.
  const { hideLogo } = useBookingFlowChrome();
  const headerRef = useRef<HTMLDivElement>(null);
  const headerHeight = SEARCH_HEADER_CONTENT_HEIGHT[isMobileView ? 'MOBILE' : 'DESKTOP'];

  return (
    <header>
      <div
        className={classnames('border-border_regular bg-surface_l2 px-sm !fixed top-0 w-full border-b')}
        style={{ zIndex: Z_INDEX.NAVBAR, top: USER_INFO_BANNER_OFFSET }}
        ref={headerRef}>
        <div
          className="gap-md container-layout bg-surface_l2 flex items-center justify-between"
          style={{ height: headerHeight }}>
          <div className="flex-1">
            {leftSlot && (
              <div className="text-m text-fg_secondary gap-sm flex items-center">
                <HeaderButton {...leftSlot.button} />
                {leftSlot.text && (
                  <span
                    {...((): HTMLAttributes<HTMLSpanElement> => {
                      const className = 'not-sr-only hidden md:block';
                      const clickProperties = leftSlot.button.onClick;
                      if (clickProperties) {
                        return { onClick: clickProperties, className: classnames(className, 'cursor-pointer') };
                      }

                      if (leftSlot.button.item.to) {
                        return {
                          onClick: () => history.push(leftSlot.button.item.to),
                          className: classnames(className, 'cursor-pointer'),
                        };
                      }
                      return { className };
                    })()}>
                    {leftSlot.text}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="gap-2xs sm:gap-3xs flex min-w-0 flex-col items-center justify-center overflow-hidden">
            <div className="text-fg_primary text-l w-full overflow-hidden text-ellipsis whitespace-nowrap text-center sm:text-xl">
              {middleSlot.title}
            </div>
            {middleSlot.subtitle && (
              <span className="text-fg_secondary text-s sm:text-m w-full overflow-hidden text-ellipsis whitespace-nowrap text-center">
                {middleSlot.subtitle}
              </span>
            )}
          </div>

          <div className="flex flex-1 items-center justify-end">
            {rightSlot && !hideLogo && <MerchantLogo {...rightSlot} alt="" size={isMobileView ? 'sm' : 'md'} />}
          </div>
        </div>
      </div>
      {/* Spacer */}
      <div className="bg-transparent" style={{ height: headerHeight }}></div>
    </header>
  );
};

export default CheckoutHeader;
